// src/pages/TermsAndServices.jsx
import React from "react";
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";

export default function TermsAndServices() {
  return (
    <main className="min-h-screen text-indigo-100">
      <Helmet>
        <title>Terms of Service — Andlegur</title>
        <meta name="description" content="Read Andlegur's Terms of Service. Understand the terms governing your use of our platform, applications, and related services." />
        <link rel="canonical" href="https://andlegur.com/terms" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Header */}
      <section className="border-b border-indigo-900/40 bg-[#060B20]">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <h1 className="text-3xl font-semibold tracking-tight text-indigo-50">
            Terms of Service — Andlegur
          </h1>
          <p className="mt-2 text-sm text-indigo-300">
            Last updated: <span className="font-medium text-indigo-200">January 9, 2026</span>
          </p>
          <p className="mt-6 text-indigo-100">
            These Terms of Service (“Terms”) govern your access to and use of the Andlegur website,
            application, content, and related offerings (collectively, the “Services”). By accessing
            or using the Services, you agree to be bound by these Terms. If you do not agree, do not
            use the Services.
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-4xl px-6 py-12">
        {/* Table of Contents */}
        <nav aria-label="Table of contents" className="mb-10 rounded-xl border border-indigo-900/40 bg-[#0A1030] p-6">
          <h2 className="text-lg font-semibold text-indigo-50">Table of Contents</h2>
          <ol className="mt-4 grid gap-2 text-sm text-indigo-300 sm:grid-cols-2">
            {[
              ["Purpose of the Services", "purpose"],
              ["Eligibility & Account Responsibility", "eligibility"],
              ["Use of Services & Acceptable Conduct", "acceptable-use"],
              ["Personalization, Guidance & Limitations", "limitations"],
              ["Information We Collect", "data-collect"],
              ["How We Use Information", "data-use"],
              ["Third-Party Sharing & Processors", "third-parties"],
              ["Payments, Subscriptions & Trials", "payments"],
              ["Cancellations & Refunds", "refunds"],
              ["Shop Purchases & Physical Products", "shop"],
              ["User Responsibilities & Liability", "user-responsibility"],
              ["Intellectual Property", "ip"],
              ["User Content & License to Andlegur", "user-content"],
              ["Community Standards", "community"],
              ["Security, Availability & Changes", "security"],
              ["Disclaimers", "disclaimers"],
              ["Limitation of Liability", "liability"],
              ["Indemnification", "indemnification"],
              ["Suspension & Termination", "termination"],
              ["Dispute Resolution & Governing Law", "disputes"],
              ["International Use & Export", "export"],
              ["Miscellaneous", "misc"],
              ["Contact", "contact"],
            ].map(([label, id]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:text-indigo-100 hover:underline">
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
  <article className="prose prose-invert prose-indigo max-w-none [&_section]:my-12 [&_h2]:text-xl [&_h2]:mb-2 [&_ul]:mt-2">
          <section id="purpose" className="scroll-mt-24">
            <h2>1. Purpose of the Services</h2>
            <p>
              Andlegur provides a digital sanctuary designed to support emotional awareness,
              reflection, and personal growth. Features may include guided practices,
              archetype companions, personalized rituals, journaling, reflective prompts, community
              features, and educational content. The Services are intended for inspiration and
              self-development; they are not medical, psychological, legal, or financial advice.
            </p>
          </section>

          <section id="eligibility" className="scroll-mt-24">
            <h2>2. Eligibility & Account Responsibility</h2>
            <ul>
              <li>You must be 16 years or older to use the Services.</li>
              <li>
                You are responsible for safeguarding your account credentials and for all activity
                that occurs under your account.
              </li>
              <li>
                You agree to provide accurate information and to promptly update it as necessary.
              </li>
            </ul>
          </section>

          <section id="acceptable-use" className="scroll-mt-24">
            <h2>3. Use of Services & Acceptable Conduct</h2>
            <p>
              You agree not to misuse the Services. Prohibited conduct includes, without limitation:
            </p>
            <ul>
              <li>Interfering with or disrupting the Services, servers, or networks.</li>
              <li>Attempting to gain unauthorized access to any systems or data.</li>
              <li>
                Uploading or transmitting malicious code, scraping, or reverse engineering except as
                permitted by law.
              </li>
              <li>
                Posting content that is unlawful, defamatory, infringing, hateful, harassing, or
                that violates privacy rights.
              </li>
              <li>Using the Services to provide professional medical or legal advice.</li>
            </ul>
          </section>

          <section id="limitations" className="scroll-mt-24">
            <h2>4. Personalization, Guidance & Limitations</h2>
            <p>
              Experiences, reflections, and archetype guidance may be personalized using your
              inputs, preferences, and activity. Outcomes cannot be guaranteed. The Services do not
              diagnose, treat, or cure any condition. If you are experiencing a crisis or medical
              emergency, contact local emergency services immediately.
            </p>
          </section>

          <section id="data-collect" className="scroll-mt-24">
            <h2>5. Information We Collect</h2>
            <p>Depending on your use, we may collect:</p>
            <ul>
              <li>
                <strong>Account & Contact Data:</strong> name, email, authentication identifiers.
              </li>
              <li>
                <strong>Profile & Preferences:</strong> archetype selections, ritual settings,
                language, time zone.
              </li>
              <li>
                <strong>Usage & Interaction Data:</strong> pages viewed, features used, session
                metadata, approximate location (derived from IP).
              </li>
              <li>
                <strong>Content You Submit:</strong> journal entries, prompts, messages, reflections
                (you control what you share).
              </li>
              <li>
                <strong>Device & Technical Data:</strong> browser type, device, operating system,
                cookies, analytics identifiers.
              </li>
            </ul>
          </section>

          <section id="data-use" className="scroll-mt-24">
            <h2>6. How We Use Information</h2>
            <p>We use information to:</p>
            <ul>
              <li>Provide, personalize, and improve the Services and user experience.</li>
              <li>Facilitate rituals, journeys, recommendations, and companion interactions.</li>
              <li>Maintain security, prevent abuse, and ensure reliability.</li>
              <li>Provide customer support and communicate service updates.</li>
              <li>
                Perform analytics, research, and product development, including with third-party
                service providers.
              </li>
              <li>Comply with legal obligations and enforce these Terms.</li>
            </ul>
            <p>
              For details on data practices, retention, and your choices, see our Privacy Policy
              (separate document).
            </p>
          </section>

          <section id="third-parties" className="scroll-mt-24">
            <h2>7. Third-Party Sharing & Processors</h2>
            <p>
              We may share data with trusted vendors and processors to operate core functionality,
              including hosting, analytics, messaging, customer support, payment processing, and
              content delivery. We may also share information:
            </p>
            <ul>
              <li>With your consent or at your direction.</li>
              <li>To comply with law, regulation, or valid legal process.</li>
              <li>To protect the rights, property, or safety of Andlegur, users, or the public.</li>
              <li>In connection with a corporate transaction, subject to safeguards.</li>
            </ul>
            <p>
              Third parties are required to handle information appropriately, but we do not control
              their policies. Review their terms and privacy practices where applicable.
            </p>
          </section>

          <section id="payments" className="scroll-mt-24">
            <h2>8. Payments, Subscriptions & Trials</h2>
            <p>
              Certain features are paid. By subscribing, you authorize the applicable charges and
              recurring billing where indicated. Pricing, taxes, and billing cycles are disclosed at
              checkout and may change with notice as required by law. If a free trial is offered, it
              will convert to a paid plan unless canceled before the trial ends.
            </p>
          </section>

          <section id="refunds" className="scroll-mt-24">
            <h2>9. Cancellations & Refunds</h2>
            
            <h3 className="text-lg font-semibold text-indigo-100 mt-4 mb-2">Subscription Refunds</h3>
            <p>
              <strong className="text-indigo-50">ALL SUBSCRIPTION PURCHASES ARE FINAL AND NON-REFUNDABLE.</strong>
            </p>
            <ul>
              <li>Free tiers are available to evaluate the Services before subscribing.</li>
              <li>By subscribing, you acknowledge that you have made an informed decision and accept full responsibility for your purchase.</li>
              <li>No refunds will be issued for dissatisfaction, change of mind, technical issues, unused time, or any other reason.</li>
              <li>If you cancel your subscription, future billing will cease, but you will not receive a refund for the current billing period.</li>
              <li>You will retain access to paid features until the end of your current paid period.</li>
              <li>Exceptions may be made only where required by applicable law or at Andlegur's sole discretion.</li>
            </ul>
            <p>
              By subscribing, you expressly waive any right to a refund and acknowledge this no-refund policy.
            </p>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">General Terms</h3>
            <ul>
              <li><strong>No Chargebacks:</strong> Initiating a chargeback or payment dispute without first attempting to resolve the matter with Andlegur may result in immediate account suspension or termination.</li>
              <li><strong>Compliance:</strong> This refund policy complies with applicable consumer protection laws. If you believe you are entitled to a refund under local law, contact us at support@andlegur.com with documentation.</li>
            </ul>
          </section>

          <section id="shop" className="scroll-mt-24">
            <h2>10. Shop Purchases & Physical Products</h2>
            <p>
              <strong className="text-indigo-50">ALL PHYSICAL PRODUCT PURCHASES ARE FINAL AND NON-REFUNDABLE.</strong>
            </p>

            <h3 className="text-lg font-semibold text-indigo-100 mt-4 mb-2">Your Responsibilities</h3>
            <ul>
              <li><strong>Informed Purchase:</strong> You are responsible for carefully reviewing product descriptions, images, specifications, pricing, and any other information before making a purchase.</li>
              <li><strong>Accurate Shipping Information:</strong> You must provide a complete and accurate shipping address. You are solely responsible for ensuring the correctness of the address provided.</li>
              <li><strong>Address Changes:</strong> Once an item has been shipped, the shipping address cannot be changed. Verify your address carefully before completing your purchase.</li>
              <li><strong>No Returns or Exchanges:</strong> We do not accept returns or exchanges for any reason, including but not limited to change of mind, incorrect size, color preference, or buyer's remorse.</li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">Shipping & Delivery</h3>
            <ul>
              <li><strong>Andlegur's Responsibility:</strong> We are responsible for packaging and shipping your order to the address you provide. We work with reputable shipping partners to ensure timely dispatch.</li>
              <li><strong>Delivery Partner's Responsibility:</strong> Once the item is shipped, delivery is handled by third-party courier and delivery partners. Andlegur is not responsible for delays, loss, damage, or non-delivery caused by the delivery partner.</li>
              <li><strong>Collaboration on Issues:</strong> If you experience delivery issues (e.g., lost package, significant delays), please contact us at support@andlegur.com. We will collaborate with you and the delivery partner to attempt to resolve the matter, but we cannot guarantee a resolution or refund.</li>
              <li><strong>Delivery Confirmation:</strong> Delivery is considered complete when the package is marked as delivered by the carrier. Any disputes regarding non-receipt must be reported within 7 days of the delivery confirmation date.</li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">Defective or Damaged Products</h3>
            <ul>
              <li>If you receive a defective or significantly damaged product, you must contact us within 7 days of delivery with photographic evidence.</li>
              <li>Andlegur will review claims on a case-by-case basis and may, at our sole discretion, offer a replacement, store credit, or other remedy.</li>
              <li>Claims submitted after 7 days will not be considered.</li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">Acknowledgment</h3>
            <p>
              By making a purchase from the Andlegur shop, you acknowledge and accept this no-refund, no-return policy, and you agree that you are making an informed, voluntary decision to purchase the product.
            </p>
          </section>

          <section id="user-responsibility" className="scroll-mt-24">
            <h2>11. User Responsibilities & Liability</h2>
            <p>
              <strong className="text-indigo-50">You are fully and solely responsible for your use of the Services.</strong> This includes, but is not limited to:
            </p>

            <h3 className="text-lg font-semibold text-indigo-100 mt-4 mb-2">Account Security</h3>
            <ul>
              <li>You are responsible for maintaining the confidentiality of your account credentials (username, password, authentication tokens).</li>
              <li>You are responsible for all activities that occur under your account, whether or not authorized by you.</li>
              <li>You must immediately notify Andlegur of any unauthorized use of your account or any other security breach.</li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">Content & Information</h3>
            <ul>
              <li>You are responsible for ensuring the accuracy, legality, and appropriateness of all content and information you submit, post, or share through the Services.</li>
              <li>You are responsible for ensuring you have all necessary rights, permissions, and consents to share content, including third-party data or personal information.</li>
              <li>You must not share sensitive, confidential, or private information of others without proper authorization.</li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">Voluntary Use & Informed Decisions</h3>
            <ul>
              <li>Your use of the Services is entirely voluntary. You acknowledge that you are making informed decisions about your participation.</li>
              <li>You understand that the Services provide informational content, guidance, and tools for personal reflection and growth, but do not constitute professional advice.</li>
              <li>You are responsible for evaluating the suitability of the Services for your needs and circumstances.</li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">Compliance & Conduct</h3>
            <ul>
              <li>You agree to comply with all applicable laws, regulations, and these Terms.</li>
              <li>You agree to respect the rights, privacy, and dignity of other users and third parties.</li>
              <li>You agree not to engage in prohibited conduct as outlined in Section 3 (Acceptable Use).</li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">Assumption of Risk</h3>
            <p>
              You acknowledge and accept that use of the Services carries inherent risks, including but not limited to:
            </p>
            <ul>
              <li>Technical issues, errors, or service interruptions.</li>
              <li>Potential exposure to content from other users that may be inaccurate, offensive, or inappropriate.</li>
              <li>Reliance on guidance, reflections, or information that may not be suitable for your specific circumstances.</li>
            </ul>
            <p>
              <strong className="text-indigo-50">YOU ASSUME ALL RISKS ASSOCIATED WITH YOUR USE OF THE SERVICES.</strong>
            </p>

            <h3 className="text-lg font-semibold text-indigo-100 mt-6 mb-2">Legal Responsibility</h3>
            <p>
              <strong className="text-indigo-50">BY USING THE SERVICES, YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS ANDLEGUR, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, AND SUPPLIERS FROM AND AGAINST ANY AND ALL CLAIMS, LIABILITIES, DAMAGES, LOSSES, COSTS, EXPENSES, OR FEES (INCLUDING REASONABLE ATTORNEYS' FEES) ARISING FROM OR RELATED TO:</strong>
            </p>
            <ul>
              <li>Your use or misuse of the Services.</li>
              <li>Your content, submissions, or communications.</li>
              <li>Your violation of these Terms or applicable laws.</li>
              <li>Your violation of any rights of another person or entity.</li>
              <li>Any harm caused to third parties as a result of your actions or content.</li>
            </ul>
            <p>
              This indemnification obligation survives termination of your account or these Terms.
            </p>
          </section>

          <section id="ip" className="scroll-mt-24">
            <h2>12. Intellectual Property</h2>
            <p>
              The Services, including designs, text, graphics, logos, icons, software, rituals,
              archetype systems, and other materials, are owned by Andlegur or its licensors and are
              protected by intellectual property laws. Except as expressly allowed, you may not copy,
              modify, distribute, or create derivative works.
            </p>
          </section>

          <section id="user-content" className="scroll-mt-24">
            <h2>13. User Content & License to Andlegur</h2>
            <p>
              You retain ownership of the content you submit. You grant Andlegur a worldwide,
              non-exclusive, royalty-free, sublicensable license to host, process, display, and
              adapt your content solely to provide and improve the Services and to operate community
              features. You are responsible for ensuring you have the rights to the content you post.
            </p>
          </section>

          <section id="community" className="scroll-mt-24">
            <h2>14. Community Standards</h2>
            <ul>
              <li>Be respectful. Do not harass, threaten, or demean others.</li>
              <li>Do not post unlawful, infringing, or harmful content.</li>
              <li>Do not impersonate others or misrepresent affiliation.</li>
              <li>Report abuse or violations through designated channels.</li>
            </ul>
          </section>

          <section id="security" className="scroll-mt-24">
            <h2>15. Security, Availability & Changes</h2>
            <p>
              We implement reasonable technical and organizational measures to protect the
              Services, but no system is fully secure. Features may evolve over time; we may add,
              change, or remove features. We may suspend or limit the Services to address security
              or operational concerns.
            </p>
          </section>

          <section id="disclaimers" className="scroll-mt-24">
            <h2>16. Disclaimers</h2>
            <p>
              The Services are provided “as is” and “as available,” without warranties of any kind,
              express or implied, including fitness for a particular purpose and non-infringement.
              Guidance, reflections, or recommendations are informational and not a substitute for
              professional advice. Outcomes are not guaranteed.
            </p>
          </section>

          <section id="liability" className="scroll-mt-24">
            <h2>17. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Andlegur and its affiliates will not be liable
              for indirect, incidental, special, consequential, punitive, or exemplary damages, or
              for lost profits, lost data, or business interruption, arising out of or related to the
              use of the Services, even if advised of the possibility of such damages. In all cases,
              Andlegur’s aggregate liability shall not exceed the amounts paid by you to Andlegur in
              the twelve (12) months preceding the claim.
            </p>
          </section>

          <section id="indemnification" className="scroll-mt-24">
            <h2>18. Indemnification</h2>
            <p>
              <strong className="text-indigo-50">YOU AGREE TO DEFEND, INDEMNIFY, AND HOLD HARMLESS ANDLEGUR AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, AND SUPPLIERS FROM AND AGAINST ANY AND ALL CLAIMS, LIABILITIES, DAMAGES, LOSSES, COSTS, EXPENSES, OR FEES (INCLUDING REASONABLE ATTORNEYS' FEES AND COURT COSTS) ARISING FROM OR IN ANY WAY RELATED TO:</strong>
            </p>
            <ul>
              <li>Your access to or use of the Services.</li>
              <li>Your content, submissions, posts, or communications.</li>
              <li>Your violation of these Terms or any applicable law or regulation.</li>
              <li>Your violation of any rights of another person or entity, including intellectual property rights, privacy rights, or publicity rights.</li>
              <li>Any harm or damage caused to third parties as a result of your actions, content, or conduct.</li>
            </ul>
            <p>
              This indemnification obligation will survive the termination of your account and these Terms.
            </p>
          </section>

          <section id="termination" className="scroll-mt-24">
            <h2>19. Suspension & Termination</h2>
            <p>
              We may suspend or terminate your access to the Services at any time, with or without
              notice, for conduct that we believe violates these Terms, applicable law, or is
              otherwise harmful. You may stop using the Services at any time. Certain provisions
              survive termination, including intellectual property, disclaimers, limitations of
              liability, and dispute resolution.
            </p>
          </section>

          <section id="disputes" className="scroll-mt-24">
            <h2>20. Dispute Resolution & Governing Law</h2>
            <p>
              These Terms are governed by the laws of New York State, without regard to its
              conflict of laws rules. Before initiating formal proceedings, the parties will attempt
              to resolve disputes in good faith. Unless prohibited by law, disputes will be resolved
              by binding arbitration on an individual basis. You may opt out of arbitration within
              thirty (30) days of accepting these Terms by sending written notice to the address
              below.
            </p>
          </section>

          <section id="export" className="scroll-mt-24">
            <h2>21. International Use & Export</h2>
            <p>
              You are responsible for complying with local laws. You agree not to use the Services
              in countries or for users or purposes prohibited by applicable export and sanctions
              laws.
            </p>
          </section>

          <section id="misc" className="scroll-mt-24">
            <h2>22. Miscellaneous</h2>
            <ul>
              <li>
                <strong>Changes to Terms:</strong> We may update these Terms from time to time. The
                latest version will be posted, and continued use constitutes acceptance.
              </li>
              <li>
                <strong>Entire Agreement:</strong> These Terms constitute the entire agreement
                between you and Andlegur regarding the Services.
              </li>
              <li>
                <strong>Severability:</strong> If any provision is found unenforceable, the
                remaining provisions will remain in full force.
              </li>
              <li>
                <strong>No Waiver:</strong> Failure to enforce a provision is not a waiver of the
                right to do so later.
              </li>
              <li>
                <strong>Assignment:</strong> You may not assign your rights; Andlegur may assign in
                connection with a merger, acquisition, or sale of assets.
              </li>
            </ul>
          </section>

          <section id="contact" className="scroll-mt-24">
            <h2>23. Contact</h2>
            <p>
              For questions about these Terms, please contact:
              <br />
              Email: support@andlegur.com
            
            </p>
          </section>
        </article>

        {/* Footer CTA */}
        <div className="mt-12 border-t border-indigo-900/40 pt-8">
          <Link
            to="/"
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            Back to Home
          </Link>
        </div>
      </section>
    </main>
  );
}
// This file defines the Terms and Services page for the Andlegur platform.
